/**
 * Hook System Types
 * Inspired by Claude Code's hook architecture
 */

export type HookType =
  | 'PreToolUse'
  | 'PostToolUse'
  | 'PermissionRequest'
  | 'SubagentStart'
  | 'SubagentStop'
  | 'SessionStart'
  | 'SessionEnd'
  | 'Notification'
  | 'PromptSubmit';

export interface HookDefinition {
  /** Unique identifier for this hook */
  id?: string;

  /** Type of hook event to trigger on */
  type: HookType;

  /** Tool name or pattern to match (for tool hooks) */
  matcher?: string | RegExp;

  /** Shell command to execute */
  command?: string;

  /** Custom timeout in milliseconds (default: 30000) */
  timeout?: number;

  /** Whether to block execution until hook completes (default: true) */
  blocking?: boolean;

  /** Environment variables to pass to command */
  env?: Record<string, string>;

  /** Working directory for command execution */
  cwd?: string;

  /** For prompt-based hooks - model to use */
  model?: string;

  /** For prompt-based hooks - the prompt template */
  prompt?: string;

  /** Whether this hook is enabled (default: true) */
  enabled?: boolean;

  /** Description for documentation */
  description?: string;
}

export interface PreToolUseInput {
  tool_name: string;
  tool_input: Record<string, any>;
  tool_use_id: string;
  session_id: string;
}

export interface PostToolUseInput {
  tool_name: string;
  tool_input: Record<string, any>;
  tool_output: string;
  tool_use_id: string;
  session_id: string;
  success: boolean;
  duration_ms: number;
}

export interface PermissionRequestInput {
  tool_name: string;
  tool_input: Record<string, any>;
  risk_level: 'low' | 'medium' | 'high';
  session_id: string;
}

export interface SubagentLifecycleInput {
  agent_id: string;
  agent_type: string;
  agent_transcript_path?: string;
  session_id: string;
  task?: string;
}

export interface SessionLifecycleInput {
  session_id: string;
  session_name?: string;
  working_directory: string;
}

export interface NotificationInput {
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  source: string;
  session_id: string;
}

export interface PromptSubmitInput {
  prompt: string;
  session_id: string;
}

export type HookInput =
  | PreToolUseInput
  | PostToolUseInput
  | PermissionRequestInput
  | SubagentLifecycleInput
  | SessionLifecycleInput
  | NotificationInput
  | PromptSubmitInput;

export interface HookResult {
  /** Whether the hook executed successfully */
  success: boolean;

  /** Output from the hook (stdout) */
  output?: string;

  /** Error message if failed */
  error?: string;

  /** Exit code from command */
  exitCode?: number;

  /** Duration of hook execution in ms */
  duration_ms: number;

  /** Whether to block the original operation */
  block?: boolean;

  /** Message to show user if blocking */
  blockMessage?: string;

  /** For permission hooks - auto decision */
  decision?: 'allow' | 'deny' | 'ask';

  /** Modified input (for pre-hooks that transform input) */
  modifiedInput?: Record<string, any>;
}

export interface HooksConfig {
  /** Global hooks enabled flag */
  enabled: boolean;

  /** Hook definitions */
  hooks: HookDefinition[];

  /** Default timeout for all hooks */
  defaultTimeout?: number;

  /** Maximum concurrent hooks */
  maxConcurrent?: number;

  /** Whether to continue on hook failure */
  continueOnError?: boolean;
}

export interface HookContext {
  /** Current working directory */
  cwd: string;

  /** Session ID */
  sessionId: string;

  /** Environment variables */
  env: Record<string, string>;

  /** User's home directory */
  homeDir: string;

  /** Project root */
  projectRoot: string;
}

// Hook event emitter types
export interface HookEvents {
  'hook:start': { hook: HookDefinition; input: HookInput };
  'hook:complete': { hook: HookDefinition; result: HookResult };
  'hook:error': { hook: HookDefinition; error: Error };
  'hook:timeout': { hook: HookDefinition };
}
